import { useCallback, useState, type ReactElement } from 'react'
import { toNumber } from 'lodash-es'
import { Box, Button, IconButton, Popover, Tooltip, useTheme } from '@mui/material'
import { EmptyState } from '@ui2/feedback'
import { MaterialSymbol } from '@ui2/icons'
import { useResultsStore } from '@editorStores'
import { RESULTS2_DETAIL_ROW_HIGHLIGHT_BG } from '../constants'
import { useHiddenSupportPosition } from '../hooks/useHiddenSupportPosition'
import { fmt, isSelectedSupportHighlightMatch, toggleSelectedSupportHighlight } from '../utils'
import { DesignSupportForceCell } from './DesignSupportForceCell'
import LoadCaseCombinationFormula from './LoadCaseCombinationFormula'

/** Which end of each support's range a row shows. */
type RangeBound = 'max' | 'min'

const RANGE_BOUNDS: RangeBound[] = ['max', 'min']

const RANGE_BOUND_LABELS: Record<RangeBound, string> = { max: 'max.', min: 'min.' }

/** One row of the table: the forces of one support under its max. or min. combinations. */
type FooterRow = {
  supportName: string
  bound: RangeBound
  /** With k_mod ("Holz"). */
  timber: DesignSupportForce
  /** Without k_mod ("GZT"). */
  gzt?: DesignSupportForce
}

type FooterSupport = {
  supportName: string
  positionRel: number
  rows: FooterRow[]
}

const GRID_TEMPLATE_COLUMNS = '32px 40px 1.4fr 1fr 1fr 1fr 60px'

export const FooterTable = ({
  elementId,
  designForces,
  designForcesWithoutKMods,
}: {
  elementId: string
  designForces: DesignSupportForceRange[]
  designForcesWithoutKMods: DesignSupportForceRange[]
}): ReactElement => {
  const theme = useTheme()
  const highlightedCheck = useResultsStore(state => state.highlightedCheck)
  const setHighlightedCheck = useResultsStore(state => state.setHighlightedCheck)

  // Hide the second support of rips/columns, matching the supports grid.
  const isHiddenSupportPosition = useHiddenSupportPosition(elementId)

  const [detailsRow, setDetailsRow] = useState<FooterRow | null>(null)
  const [detailsAnchorEl, setDetailsAnchorEl] = useState<HTMLElement | null>(null)
  const closeDetails = () => {
    setDetailsRow(null)
    setDetailsAnchorEl(null)
  }

  const handleRowClick = useCallback(
    (relativePosition: number) => () => {
      toggleSelectedSupportHighlight(
        elementId,
        relativePosition,
        highlightedCheck,
        setHighlightedCheck,
      )
    },
    [elementId, highlightedCheck, setHighlightedCheck],
  )
  const supports: FooterSupport[] = designForces
    .filter(range => !isHiddenSupportPosition(range.relative_position))
    .map(range => {
      const relativePosition = range.relative_position
      const atSamePosition = (other: DesignSupportForceRange) =>
        other.relative_position === relativePosition
      const gzt = designForcesWithoutKMods.find(atSamePosition)
      const positionRel = toNumber(relativePosition)
      return {
        supportName: range.support_name,
        positionRel,
        rows: RANGE_BOUNDS.map(bound => ({
          supportName: range.support_name,
          bound,
          timber: range[bound],
          gzt: gzt?.[bound],
        })),
      }
    })
  return (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
          fontSize: 12,
          color: 'text.secondary',
          pb: 0.5,
        }}
      >
        <Box>AL</Box>
        <Box />
        <Box>
          F<Box component="sub">d</Box> Holz [kN]
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>
            k<Box component="sub">mod</Box>
          </span>
          <Tooltip
            title="Bei Stahlbauteilen wird ein Ersatzquerschnitt mit C24 und NKL1 für die Ermittlung von k_mod und der Design-Auflagerkraft bei Holz verwendet."
            arrow
          >
            <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
              <MaterialSymbol icon="info" size={16} />
            </Box>
          </Tooltip>
        </Box>

        <Box>
          F<Box component="sub">d</Box> GZT [kN]
        </Box>
        <Box>Pos. rel.</Box>
        <Box />
      </Box>

      {supports.length === 0 ? (
        <EmptyState sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          Keine Auflagerkräfte auf dem Element vorhanden
        </EmptyState>
      ) : null}

      {supports.map((support, index) => {
        const isSupportHighlighted = isSelectedSupportHighlightMatch(
          elementId,
          highlightedCheck,
          support.positionRel,
        )

        return support.rows.map((row, rowIndex) => {
          const isFirstRow = rowIndex === 0
          const isLastRow = rowIndex === support.rows.length - 1

          return (
            <Box
              key={`${support.supportName}-${support.positionRel}-${row.bound}`}
              data-cy="design-support-force-row"
              onClick={handleRowClick(support.positionRel)}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
                fontSize: 12,
                pt: isFirstRow ? 0.625 : 0.25,
                pb: isLastRow ? 0.625 : 0.25,
                borderTop: isFirstRow ? `1px solid ${theme.palette.divider}` : 'none',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: isSupportHighlighted
                  ? RESULTS2_DETAIL_ROW_HIGHLIGHT_BG
                  : 'transparent',
              }}
            >
              <Box sx={{ fontFamily: 'Roboto' }}>{isFirstRow ? index + 1 : ''}</Box>
              <Box sx={{ color: 'text.secondary' }}>{RANGE_BOUND_LABELS[row.bound]}</Box>
              <DesignSupportForceCell force={row.timber} />
              <Box>{fmt(row.timber.k_mod)}</Box>
              <DesignSupportForceCell force={row.gzt} />
              <Box>{isFirstRow ? fmt(support.positionRel) : ''}</Box>
              <Box sx={{ display: 'inline-flex', justifyContent: 'center' }}>
                <IconButton
                  size="small"
                  title="Lastfallkombinationen"
                  onClick={e => {
                    e.stopPropagation()
                    setDetailsRow(row)
                    setDetailsAnchorEl(e.currentTarget)
                  }}
                >
                  <MaterialSymbol icon="info" size={16} />
                </IconButton>
              </Box>
            </Box>
          )
        })
      })}

      <Popover
        open={Boolean(detailsRow && detailsAnchorEl)}
        anchorEl={detailsAnchorEl}
        onClose={closeDetails}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: 'min(420px, calc(100vw - 24px))',
            borderRadius: 1.25,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Box
            sx={{
              fontFamily: 'Roboto',
              fontSize: 12.5,
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
            }}
          >
            Lastfallkombinationen
            {detailsRow
              ? ` – ${detailsRow.supportName} (${RANGE_BOUND_LABELS[detailsRow.bound]})`
              : ''}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontFamily: 'Roboto' }}>
            <Box>
              <Box sx={{ fontSize: 11, color: 'text.secondary', mb: 0.25 }}>
                F<Box component="sub">d</Box> Holz
              </Box>
              <Box sx={{ fontSize: 12, color: 'text.primary' }}>
                <LoadCaseCombinationFormula lcc={detailsRow?.timber.lcc} />
              </Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: 11, color: 'text.secondary', mb: 0.25 }}>
                F<Box component="sub">d</Box> GZT
              </Box>
              <Box sx={{ fontSize: 12, color: 'text.primary' }}>
                <LoadCaseCombinationFormula lcc={detailsRow?.gzt?.lcc} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              onClick={closeDetails}
              sx={{ textTransform: 'none', fontFamily: 'Roboto' }}
            >
              Schließen
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}
